import React, { useState, useEffect, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  IconButton,
  Typography,
  Avatar,
} from "@material-ui/core";
import {
  Close,
  ChevronLeft,
  ChevronRight,
  GetApp,
  ZoomIn,
  ZoomOut,
} from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      backgroundColor: "#000",
      margin: 0,
      maxWidth: "100%",
      maxHeight: "100%",
      width: "100%",
      height: "100%",
      borderRadius: 0,
    },
    "& .MuiBackdrop-root": {
      backgroundColor: "rgba(0,0,0,0.92)",
    },
  },
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    backgroundColor: "#111b21",
    userSelect: "none",
  },

  /* Top bar */
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    backgroundColor: "#1f2c34",
    flexShrink: 0,
    minHeight: 56,
    zIndex: 2,
  },
  topBarLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    flex: 1,
  },
  topBarAvatar: {
    width: 36,
    height: 36,
  },
  topBarInfo: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  topBarName: {
    color: "#e9edef",
    fontSize: 14,
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  topBarDate: {
    color: "#8696a0",
    fontSize: 12,
  },
  topBarActions: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    flexShrink: 0,
  },
  topBarBtn: {
    color: "#8696a0",
    padding: 8,
    "&:hover": {
      color: "#e9edef",
    },
  },

  /* Main content */
  mainArea: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    minHeight: 0,
  },
  navArrow: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 3,
    backgroundColor: "rgba(0,0,0,0.4)",
    color: "#fff",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.6)",
    },
  },
  navArrowLeft: {
    left: 16,
  },
  navArrowRight: {
    right: 16,
  },
  mediaContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    padding: 16,
  },
  mediaImage: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    borderRadius: 4,
    transition: "transform 0.2s ease",
  },
  mediaVideo: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    borderRadius: 4,
    outline: "none",
  },

  /* Thumbnail strip */
  thumbStrip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "10px 16px",
    backgroundColor: "#1f2c34",
    overflowX: "auto",
    flexShrink: 0,
    "&::-webkit-scrollbar": {
      height: 4,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#374045",
      borderRadius: 2,
    },
  },
  thumb: {
    width: 64,
    height: 64,
    objectFit: "cover",
    borderRadius: 4,
    cursor: "pointer",
    border: "2px solid transparent",
    opacity: 0.5,
    transition: "all 0.15s ease",
    flexShrink: 0,
    "&:hover": {
      opacity: 0.8,
    },
  },
  thumbActive: {
    border: "2px solid rgba(255,53,0,0.8)",
    opacity: 1,
  },
  thumbVideo: {
    position: "relative",
    width: 64,
    height: 64,
    flexShrink: 0,
    cursor: "pointer",
    borderRadius: 4,
    border: "2px solid transparent",
    opacity: 0.5,
    transition: "all 0.15s ease",
    overflow: "hidden",
    "&:hover": {
      opacity: 0.8,
    },
  },
  thumbVideoActive: {
    border: "2px solid rgba(255,53,0,0.8)",
    opacity: 1,
  },
  thumbVideoInner: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  thumbPlayIcon: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    color: "#fff",
    fontSize: 20,
    textShadow: "0 1px 4px rgba(0,0,0,0.6)",
  },
  counter: {
    color: "#8696a0",
    fontSize: 12,
    padding: "4px 0",
    textAlign: "center",
    backgroundColor: "#1f2c34",
  },
}));

const MediaViewerModal = ({
  open,
  onClose,
  mediaList,
  initialIndex,
  contact,
}) => {
  const classes = useStyles();
  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);
  const [zoom, setZoom] = useState(1);
  const [blobUrls, setBlobUrls] = useState({});
  const thumbStripRef = useRef(null);

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex || 0);
      setZoom(1);
    }
  }, [open, initialIndex]);

  // Fetch blob URLs for images (CORS proxy)
  useEffect(() => {
    if (!open || !mediaList?.length) return;
    mediaList.forEach((item, idx) => {
      if (blobUrls[item.mediaUrl]) return;
      api
        .get(item.mediaUrl, { responseType: "blob" })
        .then(({ data, headers }) => {
          const url = window.URL.createObjectURL(
            new Blob([data], { type: headers["content-type"] })
          );
          setBlobUrls((prev) => ({ ...prev, [item.mediaUrl]: url }));
        })
        .catch(() => {
          // fallback to original URL
          setBlobUrls((prev) => ({ ...prev, [item.mediaUrl]: item.mediaUrl }));
        });
    });
  }, [open, mediaList]);

  // Cleanup blob urls
  useEffect(() => {
    return () => {
      Object.values(blobUrls).forEach((url) => {
        if (url?.startsWith("blob:")) {
          window.URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  const current = mediaList?.[currentIndex];
  const resolvedUrl = current ? blobUrls[current.mediaUrl] || current.mediaUrl : "";

  const goTo = useCallback(
    (idx) => {
      if (idx >= 0 && idx < mediaList.length) {
        setCurrentIndex(idx);
        setZoom(1);
      }
    },
    [mediaList]
  );

  const goPrev = () => goTo(currentIndex - 1);
  const goNext = () => goTo(currentIndex + 1);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "Escape") onClose();
    },
    [currentIndex, mediaList]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, handleKeyDown]);

  // Scroll thumbnail into view
  useEffect(() => {
    if (thumbStripRef.current) {
      const activeThumb = thumbStripRef.current.children[currentIndex];
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      }
    }
  }, [currentIndex]);

  const handleDownload = () => {
    if (!resolvedUrl) return;
    const a = document.createElement("a");
    a.href = resolvedUrl;
    a.download = `media_${currentIndex + 1}`;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.5, 4));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.5, 0.5));

  if (!mediaList?.length) return null;

  const formatDate = (dateStr) => {
    try {
      const d = parseISO(dateStr);
      return format(d, "dd/MM/yyyy 'às' HH:mm");
    } catch {
      return "";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      className={classes.dialog}
    >
      <div className={classes.root}>
        {/* Top Bar */}
        <div className={classes.topBar}>
          <div className={classes.topBarLeft}>
            <Avatar
              className={classes.topBarAvatar}
              src={contact?.profilePicUrl}
            >
              {contact?.name?.[0]?.toUpperCase() || "?"}
            </Avatar>
            <div className={classes.topBarInfo}>
              <Typography className={classes.topBarName}>
                {contact?.name || "Contato"}
              </Typography>
              {current?.createdAt && (
                <Typography className={classes.topBarDate}>
                  {formatDate(current.createdAt)}
                </Typography>
              )}
            </div>
          </div>
          <div className={classes.topBarActions}>
            <IconButton className={classes.topBarBtn} onClick={handleZoomOut} size="small">
              <ZoomOut />
            </IconButton>
            <IconButton className={classes.topBarBtn} onClick={handleZoomIn} size="small">
              <ZoomIn />
            </IconButton>
            <IconButton className={classes.topBarBtn} onClick={handleDownload} size="small">
              <GetApp />
            </IconButton>
            <IconButton className={classes.topBarBtn} onClick={onClose} size="small">
              <Close />
            </IconButton>
          </div>
        </div>

        {/* Main media area */}
        <div className={classes.mainArea}>
          {currentIndex > 0 && (
            <IconButton
              className={`${classes.navArrow} ${classes.navArrowLeft}`}
              onClick={goPrev}
            >
              <ChevronLeft fontSize="large" />
            </IconButton>
          )}

          <div className={classes.mediaContainer}>
            {current?.mediaType === "video" ? (
              <video
                key={resolvedUrl}
                className={classes.mediaVideo}
                src={resolvedUrl}
                controls
                autoPlay
                style={{ transform: `scale(${zoom})` }}
              />
            ) : (
              <img
                className={classes.mediaImage}
                src={resolvedUrl}
                alt=""
                style={{ transform: `scale(${zoom})` }}
                draggable={false}
              />
            )}
          </div>

          {currentIndex < mediaList.length - 1 && (
            <IconButton
              className={`${classes.navArrow} ${classes.navArrowRight}`}
              onClick={goNext}
            >
              <ChevronRight fontSize="large" />
            </IconButton>
          )}
        </div>

        {/* Counter */}
        {mediaList.length > 1 && (
          <div className={classes.counter}>
            {currentIndex + 1} / {mediaList.length}
          </div>
        )}

        {/* Thumbnail filmstrip */}
        {mediaList.length > 1 && (
          <div className={classes.thumbStrip} ref={thumbStripRef}>
            {mediaList.map((item, idx) => {
              const thumbUrl = blobUrls[item.mediaUrl] || item.mediaUrl;
              if (item.mediaType === "video") {
                return (
                  <div
                    key={idx}
                    className={`${classes.thumbVideo} ${
                      idx === currentIndex ? classes.thumbVideoActive : ""
                    }`}
                    onClick={() => goTo(idx)}
                  >
                    <video
                      className={classes.thumbVideoInner}
                      src={thumbUrl}
                      muted
                      preload="metadata"
                    />
                    <span className={classes.thumbPlayIcon}>&#9654;</span>
                  </div>
                );
              }
              return (
                <img
                  key={idx}
                  className={`${classes.thumb} ${
                    idx === currentIndex ? classes.thumbActive : ""
                  }`}
                  src={thumbUrl}
                  alt=""
                  onClick={() => goTo(idx)}
                  draggable={false}
                />
              );
            })}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default MediaViewerModal;
